/**
 * Minimal QTI 2.x parser.
 *
 * Supports `<assessmentItem>` elements containing a `<choiceInteraction>`,
 * with cardinality `single` or `multiple`. Anything else (matching, gap-match,
 * extended-text, etc.) is reported as `unsupported` so the import UI can show
 * which items will be skipped.
 *
 * Accepts either a single-item document or any wrapper containing multiple
 * `<assessmentItem>` children — namespaces are tolerated by using
 * `localName`-based lookups instead of namespace-aware queries.
 */

function findChild(parent, localName) {
  if (!parent || !parent.children) return null;
  for (const c of parent.children) {
    if (c.localName === localName) return c;
  }
  return null;
}

function findAllDeep(root, localName) {
  if (!root) return [];
  const out = [];
  const walk = (node) => {
    if (!node) return;
    if (node.localName === localName) out.push(node);
    if (node.children) for (const c of node.children) walk(c);
  };
  walk(root);
  return out;
}

function plainText(node) {
  if (!node) return '';
  // Strip surrounding whitespace; keep simple inline formatting as plain text.
  return (node.textContent || '').replace(/\s+/g, ' ').trim();
}

function parseAssessmentItem(itemEl) {
  const id = itemEl.getAttribute('identifier') || '';
  const title = itemEl.getAttribute('title') || '';

  const respDecls = findAllDeep(itemEl, 'responseDeclaration');
  const interaction = findAllDeep(itemEl, 'choiceInteraction')[0];

  if (!interaction) {
    return {
      ok: false,
      reason: 'No choiceInteraction found (only single/multiple choice is supported)',
      id, title,
    };
  }

  const responseId = interaction.getAttribute('responseIdentifier') || 'RESPONSE';
  const decl = respDecls.find(d => d.getAttribute('identifier') === responseId) || respDecls[0];

  const cardinality = (decl && decl.getAttribute('cardinality')) || 'single';
  if (cardinality !== 'single' && cardinality !== 'multiple') {
    return {
      ok: false,
      reason: `Unsupported cardinality "${cardinality}"`,
      id, title,
    };
  }

  // Correct identifiers
  const correctEl = decl ? findChild(decl, 'correctResponse') : null;
  const correctIds = correctEl
    ? findAllDeep(correctEl, 'value').map(v => plainText(v))
    : [];

  // Prompt — prefer <prompt> inside the interaction; fall back to itemBody
  // text excluding choices.
  const promptEl = findAllDeep(interaction, 'prompt')[0];
  const itemBody = findChild(itemEl, 'itemBody');
  let prompt = '';
  if (promptEl) {
    prompt = plainText(promptEl);
  } else if (itemBody) {
    // Take everything in itemBody except the choiceInteraction's choices.
    const clone = itemBody.cloneNode(true);
    findAllDeep(clone, 'choiceInteraction').forEach(ci => ci.parentNode && ci.parentNode.removeChild(ci));
    prompt = plainText(clone);
  }
  if (!prompt) prompt = title || '(no prompt)';

  const choices = findAllDeep(interaction, 'simpleChoice').map(ch => ({
    id: ch.getAttribute('identifier') || '',
    text: plainText(ch),
  }));

  if (choices.length < 2) {
    return {
      ok: false,
      reason: 'Fewer than two choices',
      id, title,
    };
  }

  // A choice item with no declared correct answer cannot be imported safely —
  // it would silently arrive with every option marked incorrect. Skip it and
  // tell the user why instead of producing a broken question.
  const validCorrectIds = correctIds.filter(cid => choices.some(c => c.id === cid));
  if (validCorrectIds.length === 0) {
    return {
      ok: false,
      reason: 'No correct answer declared (empty or unmatched correctResponse)',
      id, title,
    };
  }

  // Try to extract feedback as the explanation. ModalFeedback or
  // FeedbackBlock keyed to incorrect outcomes are the typical place.
  let explanation = '';
  const feedbacks = findAllDeep(itemEl, 'modalFeedback')
    .concat(findAllDeep(itemEl, 'feedbackBlock'));
  for (const f of feedbacks) {
    const id = f.getAttribute('identifier') || '';
    if (/incorrect|wrong|hint|explanation/i.test(id)) {
      explanation = plainText(f);
      break;
    }
  }
  if (!explanation && feedbacks.length) {
    explanation = plainText(feedbacks[0]);
  }

  return {
    ok: true,
    id,
    title,
    question: {
      id: id || undefined,
      type: cardinality === 'multiple' ? 'multiple_choice' : 'single_choice',
      prompt,
      explanation,
      points: 1,
      tags: title ? [title] : [],
      options: choices.map(c => ({
        text: c.text,
        correct: validCorrectIds.includes(c.id),
      })),
    },
  };
}

/**
 * Parse a QTI XML string into a list of items.
 *
 * Each entry has either `{ ok: true, id, title, question }` or
 * `{ ok: false, id, title, reason }`.
 *
 * Throws on completely malformed XML so the caller can show a parse error.
 */
export function parseQti(xmlText) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlText, 'application/xml');
  const parseError = doc.getElementsByTagName('parsererror')[0];
  if (parseError) {
    throw new Error('XML parse error: ' + parseError.textContent.split('\n')[0]);
  }

  // Items can be top-level <assessmentItem> or referenced inside an
  // <assessmentTest>. We accept either. Just gather all of them in document
  // order.
  const items = findAllDeep(doc.documentElement, 'assessmentItem');
  if (items.length === 0) {
    // Some QTI exports use <item> as a synonym in older 1.x flavors.
    const legacy = findAllDeep(doc.documentElement, 'item');
    if (legacy.length === 0) {
      throw new Error('No <assessmentItem> elements found in this file');
    }
    return legacy.map(parseAssessmentItem);
  }
  return items.map(parseAssessmentItem);
}
